import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  BrowserControlCommunicationService,
  GmSltService,
  ServiceVisitRecordService,
} from '@applications/sha-services';
import { Actions, ofType } from '@ngrx/effects';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { DeepCopyPipe } from '@quorum/common-pipes';
import { GmSltData, MenuData, MenuServiceItem, ServiceVisitRecord } from '@quorum/models/xs-misc';
import { QuoteDetailQueryParameters } from '@quorum/models/xs-query';
import {
  AppointmentCase,
  Customer,
  Employee,
  LabourPriceCalculation,
  QuoteDetail,
  QuoteHeader,
  ServiceAppointmentDeclinedMpviCode,
  ServiceGroup,
  Vehicle,
  VehicleTemplateDetail,
  WorkOrder,
  WorkOrderCase,
  WorkorderDeclinedMpviCode,
} from '@quorum/models/xs-resource';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { FadeInOut } from '@quorum/sha-animations';
import { TwoActionDialogComponent, TwoActionDialogData } from '@quorum/sha-components-ng-ui';
import { QuoteLinesEntityActionTypes } from '@quorum/sha-entities/fixed-operations';
import { FixedOperationsStateService } from '@quorum/xsr-fixed-operations/services';
import { LabourOperationsStateService } from '@quorum/xsr-labour-operations/services';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { VehicleMaintenanceStateService } from '@quorum/xsr-vehicle-maintenance/services';
import { VehicleStateService } from '@quorum/xsr-vehicle/services';
import { ServiceLaneActionTypes } from 'libs/service-lane-root/src/lib/+state/service-lane.actions';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, from, Observable, of, Subject, Subscription, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { describeMenuLoadProblems, MENU_LOAD_TIMEOUT_MS } from './menu-load-problems';
// eslint-disable-next-line max-len
import { RoHistoryDialogComponent } from '../ro-history-dialog/ro-history-dialog.component';
import { servicesDueWithMenuData } from './services-due-with-menu-data';
import { SalesHintDialogComponent } from '../sales-hint-dialog/sales-hint-dialog.component';
// eslint-disable-next-line max-len
import { SelectDeclinedReasonDialogComponent } from '../select-declined-reason-dialog/select-declined-reason-dialog.component';
// eslint-disable-next-line max-len
import { InspectionService } from '@quorum/xsr-legacy-vip/services';
import { ServiceSelectionDialogComponent } from '../service-selection-dialog/service-selection-dialog.component';

// XS launches the menu with 0 for "no work order" / "no appointment", so only a positive id counts.
function positiveIdParam(value: string | undefined): number | null {
  const id = Number(value);
  return id > 0 ? id : null;
}

const MISSING_TECHNICIAN_SNACKBAR_DURATION_MS = 6000;

function hasTechnicianAssignment(serviceGroup: {
  technicianId?: string | null;
  technicianGroupId?: number | string | null;
}): boolean {
  return !!serviceGroup.technicianId || serviceGroup.technicianGroupId != null;
}

@Component({
  selector: 'xsr-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    FadeInOut,
    trigger('flyOutIn', [
      state(
        'shrink',
        style({
          display: 'none',
          flex: 0,
          transform: 'translateX(-160%)',
        })
      ),
      state(
        'full',
        style({
          display: 'flex',
        })
      ),
      transition('full => shrink', animate('138ms ease-in')),
      transition('shrink => full', animate('138ms ease-out')),
    ]),
    trigger('flyInOut', [
      state(
        'full',
        style({
          'box-sizing': 'border-box',
          flex: '1 1 100%',
          display: 'flex',
          width: '28%',
          'max-width': '28%',
        })
      ),
      state(
        'shrink',
        style({
          'box-sizing': 'border-box',
          flex: '1 1 100%',
          display: 'flex',
          width: '100%',
          'max-width': '100%',
        })
      ),
      transition('full => shrink', animate('138ms ease-in')),
      transition('shrink => full', animate('138ms ease-out')),
    ]),
  ],
})
export class MenuComponent implements OnInit, OnDestroy {
  $menuData: Observable<any>;
  $menuData2: Observable<any>;
  $labourOpData: Observable<any>;
  $factoryRequiredServices: Observable<any>;
  $factoryRecommendedServices: Observable<any>;
  $dealerRecommendedServices: Observable<any>;
  $vehicle: Observable<any>;
  roHistory: any = [];
  savedAccepted: any = [];
  savedDeclined: any = [];
  pendingAccepted: any = [];
  pendingDeclined: any = [];
  dank = 'full';
  requestingPending = '';
  declinedReasons: any = [];
  declinedServiceReasons: any = [];
  vehicle: Vehicle;
  dueOnlySubject: BehaviorSubject<boolean>;
  // declinedSubject: BehaviorSubject<any>;
  factoryRequiredServices: any;
  factoryRecommendedServices: any;
  dealerRecommendedServices: any;
  $workerObservable: Observable<any>;
  authenticatedUser$: Observable<any>;
  saving = false;
  saveProgress = 0;
  quote: QuoteHeader;
  readOnly: boolean;
  transaction: any;
  mileageLabel = ' ';
  transactionType: string;
  factoryRequiredServicesUi: any = { collapsed: false };
  factoryRecommendedServicesUi: any = { collapsed: false };
  dealerRecommendedServicesUi: any = { collapsed: false };
  element: any;
  actionsSubscription$: Subscription;
  disableRemoveServiceButton$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  disableAddServiceButton$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  gmSltData$: Observable<GmSltData>;
  serviceVisitRecord$: Observable<ServiceVisitRecord>;
  menuLoadProblems$: Observable<string[]>;
  private readonly menuDataError$ = new BehaviorSubject<string | null>(null);
  destroy$ = new Subject<void>();
  oem: string;
  workOrderId: number | null = null;

  constructor(
    public activatedRoute: ActivatedRoute,
    public authenticationStateService: AuthenticationStateService,
    public iconRegistry: MatIconRegistry,
    public sanitizer: DomSanitizer,
    public sgService: LabourOperationsStateService,
    public vehicleService: VehicleStateService,
    public vmService: VehicleMaintenanceStateService,
    public changeDetector: ChangeDetectorRef,
    public dialog: MatDialog,
    public fixedOperationsService: FixedOperationsStateService,
    public snackBar: MatSnackBar,
    public apiService: ApiService,
    public systemControlStateService: SystemControlStateService,
    public elementRef: ElementRef,
    public serviceLaneFacade: ServiceLaneFacade,
    public actions$: Actions,
    public browserControlService: BrowserControlCommunicationService,
    public gmSltService: GmSltService,
    public inspectionService: InspectionService,
    public serviceVisitRecordService: ServiceVisitRecordService
  ) {}

  ngOnDestroy() {
    this.actionsSubscription$.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.init();
  }

  callChangeDetection() {
    if (this.element.offsetParent !== null) {
      // element is not visible
      this.changeDetector.detectChanges();
    }
  }

  private get routeWorkOrderId(): number | null {
    return positiveIdParam(this.activatedRoute.snapshot.queryParams.workOrderId);
  }

  private get routeAppointmentId(): number | null {
    return positiveIdParam(this.activatedRoute.snapshot.queryParams.appointmentId);
  }

  private buildMenuLoadProblems(transactionObservable: Observable<unknown>): Observable<string[]> {
    const arrived = (source$: Observable<unknown>): Observable<boolean> =>
      source$.pipe(
        filter((value) => value != null),
        take(1),
        mapTo(true),
        startWith(false)
      );
    const hasTransactionId = this.routeWorkOrderId != null || this.routeAppointmentId != null;

    const errors$ = combineLatest([
      this.vmService.selectTemplateDetailError(),
      this.vmService.selectServicesDueLoadStatus(),
      this.vmService.selectRoHistoryError(),
      this.menuDataError$,
    ]);
    const arrivals$ = combineLatest([
      arrived(this.vehicleService.getVehicle(this.activatedRoute.snapshot.params.id)),
      arrived(
        this.sgService.getServiceGroups().pipe(filter((serviceGroups) => !this.sgService.isEmpty(serviceGroups)))
      ),
      arrived(transactionObservable),
      arrived(this.vmService.getTemplateDetail()),
      arrived(this.vmService.getRoHistory()),
    ]);
    const timedOut$ = timer(MENU_LOAD_TIMEOUT_MS).pipe(mapTo(true), startWith(false));

    return combineLatest([errors$, arrivals$, timedOut$]).pipe(
      map(
        ([
          [templateDetailError, servicesDue, roHistoryError, menuDataError],
          [vehicleArrived, serviceGroupsArrived, transactionArrived, templateDetailArrived, roHistoryArrived],
          timedOut,
        ]) =>
          describeMenuLoadProblems({
            templateDetailError,
            servicesDue,
            roHistoryError,
            menuDataError,
            hasTransactionId,
            transactionType: this.transactionType ?? null,
            vehicleArrived,
            serviceGroupsArrived,
            transactionArrived,
            templateDetailArrived,
            roHistoryArrived,
            timedOut,
          })
      ),
      distinctUntilChanged((previous, current) => previous.join('\n') === current.join('\n'))
    );
  }

  init() {
    if (this.browserControlService.embeddedInXsellerator && this.routeWorkOrderId) {
      this.gmSltData$ = this.loadGmSltDataFromRoute();
    } else {
      this.gmSltData$ = this.loadGmSltDataFromInspection();
    }

    this.serviceVisitRecord$ = this.buildServiceVisitRecordObservable();
    this.serviceVisitRecord$.pipe(takeUntil(this.destroy$)).subscribe();

    this.element = this.elementRef.nativeElement;
    this.fixedOperationsService
      .selectQuoteForVehicle(this.activatedRoute.snapshot.params.id)
      .pipe(
        filter((quote): quote is NonNullable<typeof quote> => quote != null),
        take(1),
        tap((quote) => {
          const quoteDetailQueryParams = new QuoteDetailQueryParameters({
            quoteHeaderId: quote.id.toString(),
            embed: 'employeeAssociate,serviceGroup',
          });

          this.serviceLaneFacade.getQuoteLinesFromServer(quoteDetailQueryParams);
          this.quote = quote;
        })
      )
      .subscribe();

    this.readOnly = this.activatedRoute.snapshot.queryParams.readOnly;
    this.fixedOperationsService.listenForToasts().subscribe((toastOptions) => {
      this.snackBar.open(toastOptions.message, toastOptions.actionText, toastOptions.options);
    });
    this.authenticatedUser$ = this.authenticationStateService.selectAuthenticatedUser();
    this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(
        filter((employee) => employee != null),
        take(1)
      )
      .subscribe((employee: Employee) => {
        this.systemControlStateService
          .getSystemControlValue('CL_DEALER_COUNTRY_ID', employee.rooftopId)
          .pipe(take(1))
          .subscribe((sct) => {
            this.mileageLabel = sct.value == 'USA' ? 'mi.' : 'km';
          });
      });

    this.dueOnlySubject = new BehaviorSubject<boolean>(true);
    // this.savedAcceptedSubject = new BehaviorSubject<any>([]);
    // this.savedDeclinedSubject = new BehaviorSubject<any>([]);
    this.iconRegistry.addSvgIcon(
      'service-drive',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/service-drive.svg')
    );

    this.$labourOpData = combineLatest([
      this.sgService.getServiceGroups(),
      this.sgService.getServiceGroupRates(),
      this.sgService.getServices(),
      this.serviceLaneFacade.quoteLines$,
    ]).pipe(
      map(([serviceGroups, serviceGroupRates, services, quoteLines]) => {
        return {
          serviceGroups: this.sgService.isEmpty(services) ? null : new DeepCopyPipe().transform(serviceGroups),
          serviceGroupRates: this.sgService.isEmpty(services) ? null : new DeepCopyPipe().transform(serviceGroupRates),
          services: this.sgService.isEmpty(services) ? null : new DeepCopyPipe().transform(services),
          quoteLines: this.sgService.isEmpty(quoteLines) ? null : new DeepCopyPipe().transform(quoteLines),
        };
      })
    );

    //CLEAN UP SUBS
    this.vmService.selectAcceptedServices().subscribe((acceptedServices: any) => {
      const runDetection = acceptedServices.length == this.savedAccepted.length ? false : true;
      this.savedAccepted = new DeepCopyPipe().transform(acceptedServices);
      if (runDetection) {
        //this.changeDetector.detectChanges();
        this.callChangeDetection();
      }
    });

    this.sgService.selectDeclinedReasons().subscribe((declinedReasons: any) => {
      this.declinedReasons = declinedReasons;
    });

    this.vmService.selectDeclinedServices().subscribe((declinedServices: any) => {
      const runDetection = declinedServices.length == this.savedDeclined.length ? false : true;
      this.savedDeclined = new DeepCopyPipe().transform(declinedServices);
      if (runDetection) {
        //this.changeDetector.detectChanges();
        this.callChangeDetection();
      }
    });

    this.vmService.selectPendingAcceptedServices().subscribe((pendingAcceptedServices: any) => {
      const runDetection = pendingAcceptedServices.length == this.pendingAccepted.length ? false : true;
      this.pendingAccepted = new DeepCopyPipe().transform(pendingAcceptedServices);
      if (runDetection) {
        //this.changeDetector.detectChanges();
        this.callChangeDetection();
      }
    });

    this.vmService.selectPendingDeclinedServices().subscribe((pendingDeclinedServices: any) => {
      const runDetection = pendingDeclinedServices.length == this.pendingDeclined.length ? false : true;
      this.pendingDeclined = new DeepCopyPipe().transform(pendingDeclinedServices);
      if (runDetection) {
        //this.changeDetector.detectChanges();
        this.callChangeDetection();
      }
    });

    //needs to be shithauled to improve performance

    this.$vehicle = this.vehicleService.getVehicle(this.activatedRoute.snapshot.params.id);

    this.$vehicle.subscribe((vehicle) => (this.vehicle = vehicle));

    let transactionObservable: Observable<any>;

    if (this.routeAppointmentId) {
      this.transactionType = 'appointment';
      transactionObservable = this.fixedOperationsService.selectAppointment(this.routeAppointmentId);
    } else if (this.routeWorkOrderId) {
      this.transactionType = 'workOrder';
      transactionObservable = this.fixedOperationsService.selectWorkOrder(this.routeWorkOrderId);
    } else {
      transactionObservable = from([null]);
    }

    transactionObservable.subscribe((trans: any) => {
      this.transaction = trans;

      // If theres no WO connected to this appt, check for and create reserved WO (needed for SLT menu data collection)
      if (this.transactionType === 'appointment' && trans) {
        this.authenticationStateService
          .selectAuthenticatedEmployee()
          .pipe(
            filter((emp) => emp != null),
            take(1),
            switchMap((employee) =>
              combineLatest([
                of(employee),
                this.systemControlStateService.getSystemControlValues(
                  ['CL_CURRENCY', 'CL_SHOP_SUPPLIES_PERCENT', 'CL_ELECTRONIC_WORK_ORDER_FLAG'],
                  employee.rooftopId
                ),
                this.serviceLaneFacade.combinedTax$.pipe(take(1)),
              ])
            )
          )
          .subscribe(([employee, systemControls, combinedTax]) => {
            this.checkOrCreateWorkOrder(trans, employee, systemControls, combinedTax)
              .pipe(take(1))
              .subscribe((workOrderId) => {
                this.workOrderId = workOrderId;

                // Create GM SLT data if this is a GM rooftop
                combineLatest([
                  this.authenticationStateService.selectAuthenticatedUser().pipe(
                    filter((authUser) => authUser != null),
                    map((authUser) => authUser.user),
                    take(1)
                  ),
                  this.apiService.get(`v/1/system/oem-rooftop-links/${employee.rooftopId}`).pipe(
                    map((oemRooftopLink) => oemRooftopLink?.associateId ?? null),
                    take(1)
                  ),
                ])
                  .pipe(take(1))
                  .subscribe(([authenticatedUser, oem]) => {
                    this.oem = oem;

                    if (oem === 'GM') {
                      this.gmSltData$ = this.fetchOrCreateGmSltData(
                        authenticatedUser.destination,
                        workOrderId.toString()
                      ).pipe(shareReplay(1));
                    }
                  });
              });
          });
      }
    });

    this.menuLoadProblems$ = this.buildMenuLoadProblems(transactionObservable);

    this.$menuData2 = forkJoin([
      this.vmService.getServicesDue().pipe(
        map((servicesDue) => {
          return new DeepCopyPipe().transform(servicesDue);
        }),
        take(1)
      ),
      this.sgService.getServiceGroups().pipe(
        filter((serviceGroups) => !this.sgService.isEmpty(serviceGroups)),
        map((serviceGroups) => {
          return new DeepCopyPipe().transform(serviceGroups);
        }),
        take(1)
      ),
      this.vmService.getTemplateDetail().pipe(
        map((templateDetail) => {
          return new DeepCopyPipe().transform(templateDetail);
        }),
        take(1)
      ),
      this.vehicleService.getVehicle(this.activatedRoute.snapshot.params.id).pipe(take(1)),
      transactionObservable.pipe(
        filter((trans) => trans != null),
        take(1)
      ),
    ]).pipe(
      map(([allServicesDue, serviceGroups, templateDetail, vehicle, transaction]) => {
        const servicesDue = servicesDueWithMenuData(allServicesDue, serviceGroups, templateDetail);
        servicesDue.forEach((serviceDue: any) => {
          serviceDue.collapsed = false;
          serviceDue.vehicleMileage = transaction.odometerIn ? transaction.odometerIn : 0;
          serviceDue.serviceGroup = new DeepCopyPipe().transform(serviceGroups[serviceDue.serviceGroupId]);
          serviceDue.displayServiceGroup = new DeepCopyPipe().transform(serviceGroups[serviceDue.serviceGroupId]);
          serviceDue.frequencyMileage = templateDetail.find(
            (templateDetail: VehicleTemplateDetail) => templateDetail.serviceGroupId === serviceDue.serviceGroupId
          ).frequencyMileage;
          serviceDue.frequencyMonths = templateDetail.find(
            (templateDetail: VehicleTemplateDetail) => templateDetail.serviceGroupId === serviceDue.serviceGroupId
          ).frequencyMonths;
          let date = new Date();
          date.setDate(new Date().getDate() + 21);
          if (
            serviceDue.overdue ||
            new Date(serviceDue.date).getTime() < date.getTime() ||
            serviceDue.vehicleMileage > serviceDue.mileage
          ) {
            serviceDue.dueDate = new Date();
            if (serviceDue.vehicleMileage > serviceDue.mileage) {
              serviceDue.dueBy = ' by Mileage';
            } else {
              serviceDue.dueBy = ' by Date';
            }
          } else {
            serviceDue.dueDate = new Date(serviceDue.date);
            serviceDue.dueBy = ' by Date';
          }

          serviceDue.serviceTypeId = templateDetail.find(
            (templateDetail: VehicleTemplateDetail) => templateDetail.serviceGroupId === serviceDue.serviceGroupId
          ).serviceTypeId;
          if (serviceDue.serviceGroup.useFlag === 'M') {
            for (var i = 0; i < serviceDue.serviceGroup.mappedServiceGroups.length; i++) {
              serviceDue.serviceGroup.mappedServiceGroups[i] =
                serviceGroups[serviceDue.serviceGroup.mappedServiceGroups[i]];
            }
            serviceDue.serviceGroup.mappedServiceGroups = serviceDue.serviceGroup.mappedServiceGroups.filter(
              (msg: any) => {
                if (msg) {
                  return msg.expiryDate == null || new Date(msg.expiryDate) > new Date();
                }
              }
            );
          }
        });

        return servicesDue;
      }),
      catchError((error: unknown) => {
        console.error('Error', error);
        this.menuDataError$.next(
          "The services due for this vehicle do not match its maintenance schedule template, so the menu can't be built."
        );
        return EMPTY;
      }),
      shareReplay(1)
    );

    this.$menuData = combineLatest([this.$menuData2, transactionObservable, this.vmService.getRoHistory()]).pipe(
      map(([servicesDue, transaction, roHistory]) => {
        servicesDue.forEach((serviceDue: any) => {
          if (
            transaction.embedded &&
            (this.transactionType == 'workOrder'
              ? transaction.embedded.cases != null
                ? transaction.embedded.cases
                : []
              : transaction.embedded.cases != null
              ? transaction.embedded.cases
              : []
            )
              .map((c: any) => {
                return c.serviceGroupId;
              })
              .indexOf(serviceDue.serviceGroup.id) > -1
          ) {
            this.vmService.addAcceptedService(serviceDue.serviceGroup);
          }

          if (
            transaction.embedded &&
            (this.transactionType == 'workOrder'
              ? transaction.embedded.declinedMpviCodes != null
                ? transaction.embedded.declinedMpviCodes
                : []
              : transaction.embedded.serviceAppointmentDeclinedMpviCodes != null
              ? transaction.embedded.serviceAppointmentDeclinedMpviCodes
              : []
            ).filter((declinedService: any) => {
              return (
                declinedService.opCode == serviceDue.serviceGroup.id ||
                declinedService.opCode == serviceDue.serviceGroup.workOrderLocationOpCode
              );
            }).length > 0
          ) {
            this.vmService.addDeclinedService(serviceDue.serviceGroup);
          }

          if (serviceDue.serviceGroup.useFlag === 'M') {
            let serviceAcceptedFound = false;
            let serviceDeclinedFound = false;

            for (var i = 0; i < serviceDue.serviceGroup.mappedServiceGroups.length; i++) {
              if (
                !serviceAcceptedFound &&
                transaction.embedded &&
                (this.transactionType == 'workOrder'
                  ? transaction.embedded.cases != null
                    ? transaction.embedded.cases
                    : []
                  : transaction.embedded.cases != null
                  ? transaction.embedded.cases
                  : []
                )
                  .map((c: any) => {
                    return c.serviceGroupId;
                  })
                  .indexOf(serviceDue.serviceGroup.mappedServiceGroups[i].id) > -1
              ) {
                this.vmService.addAcceptedService(serviceDue.serviceGroup.mappedServiceGroups[i]);

                let factoryRequiredService = servicesDue.findIndex((s: any) => {
                  return s.id == serviceDue.id && s.serviceTypeId === 1;
                });

                let factoryRecommendedService: any = servicesDue.findIndex((s: any) => {
                  return s.id == serviceDue.id && s.serviceTypeId === 2;
                });

                let dealerRecommendedService: any = servicesDue.findIndex((s: any) => {
                  return s.id == serviceDue.id && s.serviceTypeId === 3;
                });

                if (factoryRequiredService > -1) {
                  servicesDue[factoryRequiredService].displayServiceGroup =
                    serviceDue.serviceGroup.mappedServiceGroups[i];
                } else if (factoryRecommendedService > -1) {
                  servicesDue[factoryRecommendedService].displayServiceGroup =
                    serviceDue.serviceGroup.mappedServiceGroups[i];
                } else if (dealerRecommendedService > -1) {
                  servicesDue[dealerRecommendedService].displayServiceGroup =
                    serviceDue.serviceGroup.mappedServiceGroups[i];
                }

                serviceAcceptedFound = true;
              }

              if (
                !serviceDeclinedFound &&
                transaction.embedded &&
                (this.transactionType == 'workOrder'
                  ? transaction.embedded.declinedMpviCodes != null
                    ? transaction.embedded.declinedMpviCodes
                    : []
                  : transaction.embedded.serviceAppointmentDeclinedMpviCodes != null
                  ? transaction.embedded.serviceAppointmentDeclinedMpviCodes
                  : []
                ).filter((declinedService: any) => {
                  return (
                    declinedService.opCode == serviceDue.serviceGroup.mappedServiceGroups[i].id ||
                    declinedService.opCode == serviceDue.serviceGroup.mappedServiceGroups[i].workOrderLocationOpCode
                  );
                }).length > 0
              ) {
                this.vmService.addDeclinedService(serviceDue.serviceGroup.mappedServiceGroups[i]);

                let factoryRequiredService: any = servicesDue.findIndex((s: any) => {
                  return s.id == serviceDue.id && s.serviceTypeId === 1;
                });

                let factoryRecommendedService: any = servicesDue.findIndex((s: any) => {
                  return s.id == serviceDue.id && s.serviceTypeId === 2;
                });

                let dealerRecommendedService: any = servicesDue.findIndex((s: any) => {
                  return s.id == serviceDue.id && s.serviceTypeId === 3;
                });

                if (factoryRequiredService > -1) {
                  servicesDue[factoryRequiredService].displayServiceGroup =
                    serviceDue.serviceGroup.mappedServiceGroups[i];
                } else if (factoryRecommendedService > -1) {
                  servicesDue[factoryRecommendedService].displayServiceGroup =
                    serviceDue.serviceGroup.mappedServiceGroups[i];
                } else if (dealerRecommendedService > -1) {
                  servicesDue[dealerRecommendedService].displayServiceGroup =
                    serviceDue.serviceGroup.mappedServiceGroups[i];
                }

                serviceDeclinedFound = true;
              }
              // if (
              //   roHistory.findIndex(
              //     (history: any) => history.serviceGroupId == serviceDue.serviceGroup.mappedServiceGroups[i].id
              //   ) > -1
              // ) {
              //   let factoryRequiredService: any = servicesDue.findIndex((s: any) => {
              //     return s.id == serviceDue.id && s.serviceTypeId === 1;
              //   });

              //   let factoryRecommendedService: any = servicesDue.findIndex((s: any) => {
              //     return s.id == serviceDue.id && s.serviceTypeId === 2;
              //   });

              //   let dealerRecommendedService: any = servicesDue.findIndex((s: any) => {
              //     return s.id == serviceDue.id && s.serviceTypeId === 3;
              //   });

              //   if (factoryRequiredService > -1) {
              //     servicesDue[factoryRequiredService].displayServiceGroup =
              //       serviceDue.serviceGroup.mappedServiceGroups[i];
              //   } else if (factoryRecommendedService > -1) {
              //     servicesDue[factoryRecommendedService].displayServiceGroup =
              //       serviceDue.serviceGroup.mappedServiceGroups[i];
              //   } else if (dealerRecommendedService > -1) {
              //     servicesDue[dealerRecommendedService].displayServiceGroup =
              //       serviceDue.serviceGroup.mappedServiceGroups[i];
              //   }
              // }
            }
          }
        });

        this.changeDetector.detectChanges();
        this.vmService.servicesDue$.next(servicesDue);
        return servicesDue;
      }),
      take(1),
      shareReplay(1)
    );

    //search thru due services and see if any already been accepted/declined on current transaction
    function sameDay(d1: Date, d2: Date) {
      return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    }

    let determineServices = map(([servicesDue, onlyDue]) => {
      let filteredServices = servicesDue.filter((serviceDue: any) => {
        if (
          (onlyDue && (sameDay(serviceDue.dueDate, new Date()) || serviceDue.vehicleMileage > serviceDue.mileage)) ||
          !onlyDue
        ) {
          return serviceDue;
        }
      });
      return new DeepCopyPipe().transform(filteredServices);
    });

    let getServicesOfType = function (typeId: number) {
      return map((servicesDue: any) => {
        let returnVal = servicesDue.filter((serviceDue: any) => {
          return serviceDue.serviceTypeId === typeId;
        });
        return returnVal;
      });
    };

    this.$factoryRequiredServices = combineLatest([
      this.$menuData.pipe(getServicesOfType(1)),
      this.dueOnlySubject,
    ]).pipe(determineServices);

    this.$factoryRequiredServices.subscribe((factoryRequiredServices) => {
      this.factoryRequiredServices = new DeepCopyPipe().transform(factoryRequiredServices);
      //this.changeDetector.detectChanges();
      this.callChangeDetection();
    });

    this.$factoryRecommendedServices = combineLatest([
      this.$menuData.pipe(getServicesOfType(2)),
      this.dueOnlySubject,
    ]).pipe(determineServices);

    this.$factoryRecommendedServices.subscribe((factoryRecommendedServices) => {
      this.factoryRecommendedServices = factoryRecommendedServices;
      //this.changeDetector.detectChanges();
      this.callChangeDetection();
    });

    this.$dealerRecommendedServices = combineLatest([
      this.$menuData.pipe(getServicesOfType(3)),
      this.dueOnlySubject,
    ]).pipe(determineServices);

    this.$dealerRecommendedServices.subscribe((dealerRecommendedServices) => {
      this.dealerRecommendedServices = dealerRecommendedServices;

      //this.changeDetector.detectChanges();
      this.callChangeDetection();
    });

    this.vmService.getRoHistory().subscribe((roHistory) => {
      this.roHistory = roHistory;
      //this.changeDetector.detectChanges();
      this.callChangeDetection();
    });
    this.disableRemoveServiceButton(false);
    this.disableAddServiceButton(false);

    this.actionsSubscription$ = this.actions$
      .pipe(
        ofType(
          ServiceLaneActionTypes.UpdateApprovedQuoteLineInState,
          QuoteLinesEntityActionTypes.DeleteQuoteLineFromServerSuccess,
          QuoteLinesEntityActionTypes.CreateQuoteLineOnServerSuccess,
          QuoteLinesEntityActionTypes.CreateQuoteLineOnServerError
        ),
        map((action: any) => {
          switch (action.type) {
            case ServiceLaneActionTypes.UpdateApprovedQuoteLineInState:
            case QuoteLinesEntityActionTypes.CreateQuoteLineOnServerError:
              this.disableRemoveServiceButton(false);
              break;
            case QuoteLinesEntityActionTypes.DeleteQuoteLineFromServerSuccess:
              this.disableAddServiceButton(false);
              break;
            case QuoteLinesEntityActionTypes.CreateQuoteLineOnServerSuccess:
              let toastOptions: any = {
                message: 'Line added to quote',
                actionText: null,
                options: { duration: 3000 },
              };
              this.serviceLaneFacade.addToast(toastOptions);
              break;
            default:
              break;
          }
        })
      )
      .subscribe();
  }

  private loadGmSltDataFromRoute(): Observable<GmSltData | null> {
    const workOrderId$ = of(String(this.routeWorkOrderId));
    return this.buildGmSltDataObservable(workOrderId$);
  }

  private loadGmSltDataFromInspection(): Observable<GmSltData | null> {
    const workOrderId$ = this.inspectionService.workOrderId$.pipe(
      filter((workOrderId) => !!workOrderId),
      take(1)
    );
    return this.buildGmSltDataObservable(workOrderId$);
  }

  private buildGmSltDataObservable(workOrderId$: Observable<string>): Observable<GmSltData | null> {
    return combineLatest([workOrderId$, this.getAuthenticatedUser(), this.getOemType()]).pipe(
      switchMap(([workOrderId, authenticatedUser, oem]) => {
        this.oem = oem;

        if (oem === 'GM') {
          return this.fetchOrCreateGmSltData(authenticatedUser.destination, workOrderId);
        }
        return of(null);
      })
    );
  }

  private getAuthenticatedUser(): Observable<any> {
    return this.authenticationStateService.selectAuthenticatedUser().pipe(
      filter((authenticatedUser) => authenticatedUser !== null),
      map((authUser) => authUser.user),
      take(1)
    );
  }

  private getOemType(): Observable<string | null> {
    return this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      filter((employee) => employee !== null),
      switchMap((employee) =>
        this.apiService
          .get(`v/1/system/oem-rooftop-links/${employee.rooftopId}`)
          .pipe(map((oemRooftopLink) => oemRooftopLink?.associateId ?? null))
      ),
      take(1)
    );
  }

  private fetchOrCreateGmSltData(destination: string, workOrderId: string): Observable<GmSltData> {
    return this.gmSltService.getOrCreateGmSltData(destination, workOrderId);
  }

  private buildServiceVisitRecordObservable(): Observable<ServiceVisitRecord> {
    const workOrderId$ =
      this.browserControlService.embeddedInXsellerator && this.routeWorkOrderId
        ? of(String(this.routeWorkOrderId))
        : this.inspectionService.workOrderId$.pipe(
            filter((workOrderId) => !!workOrderId),
            take(1)
          );

    return combineLatest([workOrderId$, this.getAuthenticatedUser()]).pipe(
      switchMap(([workOrderId, authenticatedUser]) => {
        return this.serviceVisitRecordService.getOrCreateServiceVisitRecord(
          authenticatedUser.destination,
          this.routeAppointmentId?.toString(),
          workOrderId?.toString()
        );
      }),
      switchMap((svr: ServiceVisitRecord) => {
        if (svr.communications?.advisorMobileCheckIn) {
          return of(svr);
        }
        return this.serviceVisitRecordService.recordAdvisorMobileCheckIn(svr);
      }),
      switchMap((svr: ServiceVisitRecord) => {
        return this.serviceVisitRecordService.recordInitialSnapshot(svr);
      })
    );
  }

  acceptServiceOnQuote(serviceGroup: any, labourOpData: any, employeeId: string) {
    // pCreateQuoteDetail refuses a line with neither a technician nor a technician group.
    if (!hasTechnicianAssignment(serviceGroup)) {
      this.snackBar.open(
        `"${serviceGroup.name}" has no technician or technician group. Assign one to the service group before adding it to a quote.`,
        undefined,
        { duration: MISSING_TECHNICIAN_SNACKBAR_DURATION_MS }
      );
      return;
    }
    if (this.quote !== null) {
      let quoteDetailQueryParams: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
      quoteDetailQueryParams.quoteHeaderId = this.quote.id.toString();
      quoteDetailQueryParams.serviceGroupId = serviceGroup.id;
      this.apiService
        .get<QuoteDetail[]>(`v/1/fixed-operations/quote-details`, {
          params: quoteDetailQueryParams,
        })
        .pipe(
          map((quoteDetails: QuoteDetail[]) => {
            return quoteDetails.findIndex((ql) => ql.sourceType == null && ql.sourceReferenceId == null) > -1
              ? true
              : false;
          })
        )
        .subscribe((quoteLineExists) => {
          if (!quoteLineExists) {
            this.confirmAddServiceToQuote(serviceGroup, labourOpData, employeeId);
          } else {
            this.snackBar.open('Quote line already exists on quote. Cannot add duplicate.');
          }
        });
    } else {
      this.confirmAddServiceToQuote(serviceGroup, labourOpData, employeeId);
    }
  }

  // serviceGroup and labourOpData are the menu template's untyped view models; ServiceGroup does not match their runtime shape.
  private confirmAddServiceToQuote(serviceGroup: any, labourOpData: any, employeeId: string): void {
    const dialogData: TwoActionDialogData = {
      actionTwoCaption: 'Add Service',
      actionOneCaption: 'Cancel',
      content: 'This service will be added to the associated quote.',
      title: 'Add Service?',
    };

    const dialogRef = this.dialog.open(TwoActionDialogComponent, {
      width: '400px',
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
      if (result) {
        const quoteDetail: QuoteDetail = new QuoteDetail();
        quoteDetail.rooftopId = serviceGroup.rooftopId;
        quoteDetail.employeeId = employeeId;
        quoteDetail.associateId = employeeId;
        //quoteDetail.quoteHeaderId = this.quote.id;
        quoteDetail.serviceGroupId = serviceGroup.id;
        quoteDetail.serviceName;
        quoteDetail.serviceName = serviceGroup.name + ' (';
        serviceGroup.services.forEach((service: any) => {
          quoteDetail.serviceName += ' ' + labourOpData.services[service].name;
        });
        quoteDetail.serviceName += ')';
        quoteDetail.labourOpCode = serviceGroup.workOrderLocationOpCode;
        quoteDetail.recommendedHours = serviceGroup.serviceGroupRateId
          ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].recommendedHours != null
            ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].recommendedHours
            : serviceGroup.recommendedHours
          : serviceGroup.recommendedHours;

        quoteDetail.technicianGroupId = serviceGroup.technicianGroupId;
        quoteDetail.technicianGroupName = serviceGroup.technicianGroupName;
        quoteDetail.technicianId = serviceGroup.technicianId;
        quoteDetail.warrantySubmissionTypeId =
          serviceGroup.warrantySubmissionTypeId != null && serviceGroup.warrantySubmissionTypeId.trim() != ''
            ? serviceGroup.warrantySubmissionTypeId
            : 'CP';

        quoteDetail.rateType = labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId]
          ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].rateType
            ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].rateType
            : null
          : null;

        quoteDetail.rate = labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId]
          ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].rate
            ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].rate
            : null
          : null;
        quoteDetail.totalAmount = labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId]
          ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].rate
            ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].rate
            : null
          : null;
        quoteDetail.shopSupplyFlag = labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId]
          ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].shopSupplyFlag
            ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].shopSupplyFlag
            : null
          : null;
        quoteDetail.lineAddedFromServiceSchedule = 1;
        quoteDetail.expirationDate = new Date();
        quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);
        if (this.transactionType === 'workOrder') {
          quoteDetail.workHeaderId = this.transaction.id;
        }
        if (this.quote == null) {
          const quoteHeader: QuoteHeader = new QuoteHeader();
          quoteHeader.vehicleId = this.vehicle.id;
          quoteHeader.vin = this.vehicle.vin;
          quoteHeader.rooftopId = this.vehicle.rooftopId;
          quoteHeader.associateId = this.vehicle.associateId;
          quoteHeader.licenseNumber = this.vehicle.licensePlateNumber;

          this.fixedOperationsService.createQuoteAndCase(quoteHeader, quoteDetail);
        } else {
          quoteDetail.quoteHeaderId = this.quote.id;
          this.fixedOperationsService.createQuoteCase(quoteDetail);
        }
      }
    });
  }

  acceptService(menuItem: any, labourOpData: any, gmSltData: GmSltData): void {
    const serviceGroup: ServiceGroup = menuItem.displayServiceGroup;
    const mileageInterval: number = menuItem.mileage;
    const serviceTypeId: number = menuItem.serviceTypeId;
    // Check if quote exists first
    const quoteCheck$ = this.serviceLaneFacade
      .quoteLineWithServiceGroupExists(serviceGroup.id, this.routeAppointmentId)
      .pipe(
        take(1),
        tap((quoteExists) => {
          if (!quoteExists) {
            this.disableRemoveServiceButton(true);
          }
        })
      );

    // Fetch labour rate data
    const labourData$ = combineLatest([
      this.apiService.get<Customer>(`/v/1/associates/customers/${this.transaction.customerId}`).pipe(
        map((associateCustomer) => associateCustomer.labourRateHour),
        catchError(() => of(null))
      ),
      this.apiService
        .get<LabourPriceCalculation[]>('v/1/fixed-operations/labour-price-calculations')
        .pipe(catchError(() => of([] as LabourPriceCalculation[]))),
    ]).pipe(
      map(([labourRateHour, labourRateMaintenance]) => ({
        labourRateHour,
        labourRateMaintenance,
      }))
    );

    combineLatest([quoteCheck$, labourData$])
      .pipe(
        take(1),
        catchError((error) => {
          console.error('Error accepting service:', error);
          return of(null);
        }),
        switchMap((result) => {
          if (!result) {
            throw new Error('Failed to fetch required data');
          }

          const [quoteExists, data] = result;
          const rate = this.calculateRate(serviceGroup, data);
          const enrichedServiceGroup = this.enrichServiceGroup(serviceGroup, labourOpData, rate);

          this.vmService.addPendingAcceptedService(enrichedServiceGroup);
          this.vmService.scheduleSavedNotification.next(true);
          this.callChangeDetection();

          // Fetch the LATEST gmSltData from API before updating
          if (gmSltData) {
            return this.gmSltService
              .getGmSltByWorkOrderId(this.extractDestinationId(gmSltData), gmSltData.workOrderId)
              .pipe(
                take(1),
                switchMap((latestGmSltData) => {
                  // Check if laborOperationId already exists in the LATEST data
                  const laborOperationId = this.getLaborOperationId(serviceGroup);

                  if (!laborOperationId) {
                    return of(null);
                  }

                  if (this.laborOperationIdExists(latestGmSltData, laborOperationId)) {
                    return of(null);
                  }

                  // Update with LATEST data
                  const updatedGmSltData = this.updateGmSltMenuData(
                    latestGmSltData,
                    mileageInterval,
                    serviceGroup,
                    serviceTypeId
                  );

                  // Return the GM SLT update observable
                  return this.gmSltService.updateGmSltRecord(updatedGmSltData);
                })
              );
          } else {
            return of(null);
          }
        }),
        take(1),
        catchError((error) => {
          console.error('Error accepting service:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  // Extract rate calculation to separate method for clarity
  private calculateRate(
    serviceGroup: any,
    data: { labourRateHour: number | null; labourRateMaintenance: LabourPriceCalculation[] }
  ): any {
    if (data.labourRateHour) {
      return {
        rate: data.labourRateHour,
        rateType: '1',
        serviceGroupId: serviceGroup.id,
      };
    }

    const labourTypeId = this.mapWarrantyTypeToLabourType(serviceGroup.warrantySubmissionTypeId);
    const matchingRate = this.findActiveLabourRate(data.labourRateMaintenance, labourTypeId);

    return {
      rate: matchingRate?.rate,
      rateType: '1',
      serviceGroupId: serviceGroup.id,
    };
  }

  // Extract mapping logic for clarity
  private mapWarrantyTypeToLabourType(warrantySubmissionTypeId: string): string {
    const warrantyToLabourMap: Record<string, string> = {
      CP: 'R',
      WC: 'W',
      IN: 'I',
      EC: 'EW',
    };
    return warrantyToLabourMap[warrantySubmissionTypeId] || '';
  }

  // Extract filtering logic
  private findActiveLabourRate(
    labourRates: LabourPriceCalculation[],
    labourTypeId: string
  ): LabourPriceCalculation | undefined {
    const now = new Date();
    return labourRates.find(
      (labour) =>
        labour.rateBasis === 1 &&
        labour.labourTypeId === labourTypeId &&
        now >= new Date(labour.effectiveDate) &&
        now <= new Date(labour.expiryDate)
    );
  }

  // Extract service group enrichment
  private enrichServiceGroup(serviceGroup: ServiceGroup, labourOpData: any, rate: any): ServiceGroup {
    // Create a deep copy to avoid mutating the input
    const enrichedGroup = new DeepCopyPipe().transform(serviceGroup);

    if (!enrichedGroup.embedded) {
      enrichedGroup.embedded = {};
    }

    enrichedGroup.embedded.services = enrichedGroup.services.map((service: string) => labourOpData.services[service]);

    enrichedGroup.embedded.rate = enrichedGroup.serviceGroupRateId
      ? labourOpData.serviceGroupRates[enrichedGroup.serviceGroupRateId]
      : rate;

    return enrichedGroup;
  }

  private updateGmSltMenuData(
    gmSltData: GmSltData,
    mileageInterval: number,
    serviceGroup: any,
    serviceTypeId: number
  ): GmSltData {
    // Create a copy to avoid mutating the input
    const updatedData = {
      ...gmSltData,
      menuData: gmSltData.menuData ? gmSltData.menuData.slice() : [],
    };

    updatedData.menuData.push({
      menuType: serviceTypeId === 1 || serviceTypeId === 2 ? 'F' : 'D',
      mileageMarker: mileageInterval?.toString() ?? '',
      laborOperationId: serviceGroup.workOrderLocationOpCode?.toString() ?? '',
    });

    return updatedData;
  }

  addQuoteLineToServer(menuItem: any, labourOpData: any, gmSltData: GmSltData) {
    const mileageInterval: number = menuItem.mileage;
    const serviceTypeId: number = menuItem.serviceTypeId;
    const serviceGroup = new DeepCopyPipe().transform(menuItem.displayServiceGroup);
    serviceGroup.embedded ? null : (serviceGroup.embedded = {});
    serviceGroup.embedded.services = [];
    serviceGroup.services.forEach((service: any) => {
      serviceGroup.embedded.services.push(labourOpData.services[service]);
    });
    serviceGroup.embedded.rate = labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId];
    this.serviceLaneFacade.addQuoteLineFromMenu$.next(serviceGroup);

    // Fetch the LATEST gmSltData from API before updating
    if (gmSltData) {
      this.gmSltService
        .getGmSltByWorkOrderId(this.extractDestinationId(gmSltData), gmSltData.workOrderId)
        .pipe(
          take(1),
          switchMap((latestGmSltData) => {
            // Check if laborOperationId already exists in the LATEST data
            const laborOperationId = this.getLaborOperationId(serviceGroup);

            if (!laborOperationId) {
              return of(null);
            }

            if (this.laborOperationIdExists(latestGmSltData, laborOperationId)) {
              return of(null);
            }

            // Update with LATEST data
            const updatedGmSltData = this.updateGmSltMenuData(
              latestGmSltData,
              mileageInterval,
              serviceGroup,
              serviceTypeId
            );

            // Return the GM SLT update observable
            return this.gmSltService.updateGmSltRecord(updatedGmSltData);
          }),
          catchError((error) => {
            console.error('Error updating GM SLT after adding quote line:', error);
            return of(null);
          })
        )
        .subscribe();
    }
  }

  sliderChanged(event: any) {
    this.dueOnlySubject.next(event.checked);
  }
  toggleAnimation() {
    this.dank = this.dank === 'full' ? 'shrink' : 'full';
  }

  // `quoteLines` is null when the transaction has no quote lines yet (see $labourOpData).
  removeAcceptedService(menuItem: any, quoteLines: QuoteDetail[] | null, gmSltData: GmSltData) {
    this.disableAddServiceButton(true);

    const lines = quoteLines ?? [];
    const selectedQuoteLine = lines[lines.map((ql) => ql.serviceGroupId).lastIndexOf(menuItem.displayServiceGroup.id)];

    for (let i = 0; i <= this.pendingAccepted.length - 1; i++) {
      if (this.pendingAccepted[i].id === menuItem.displayServiceGroup.id) {
        this.vmService.removePendingAcceptedService(menuItem.displayServiceGroup.id);
        menuItem.displayServiceGroup = menuItem.serviceGroup;
        this.callChangeDetection();
      }
    }

    if (selectedQuoteLine) {
      this.serviceLaneFacade.removeApprovedQuoteLine(selectedQuoteLine);
      this.serviceLaneFacade.deleteQuoteLineFromServer(selectedQuoteLine);
    }

    // Remove from GM SLT record
    const laborOperationId = menuItem.displayServiceGroup.workOrderLocationOpCode?.toString() ?? '';

    if (laborOperationId && gmSltData) {
      // Fetch latest GM SLT data
      this.gmSltService
        .getGmSltByWorkOrderId(this.extractDestinationId(gmSltData), gmSltData.workOrderId)
        .pipe(
          take(1),
          switchMap((latestGmSltData) => {
            if (!latestGmSltData || !latestGmSltData.menuData) {
              return of(null);
            }

            const updatedGmSltData = this.removeFromGmSltMenuData(latestGmSltData, laborOperationId);

            if (!updatedGmSltData) {
              return of(null);
            }

            return this.gmSltService.updateGmSltRecord(updatedGmSltData);
          }),
          catchError((error) => {
            console.error('Error removing service from GM SLT:', error);
            return of(null);
          })
        )
        .subscribe();
    }
  }

  declineService(menuItem: any, gmSltData: GmSltData) {
    const serviceGroup: ServiceGroup = menuItem.displayServiceGroup;
    const mileageInterval: number = menuItem.mileage;
    const serviceTypeId: number = menuItem.serviceTypeId;
    this.vmService.addPendingDeclinedService(serviceGroup);

    // Fetch the LATEST gmSltData from API before updating
    if (gmSltData) {
      this.gmSltService
        .getGmSltByWorkOrderId(this.extractDestinationId(gmSltData), gmSltData.workOrderId)
        .pipe(
          take(1),
          switchMap((latestGmSltData) => {
            // Check if laborOperationId already exists in the LATEST data
            const laborOperationId = this.getLaborOperationId(serviceGroup);

            if (!laborOperationId) {
              return of(null);
            }

            if (this.laborOperationIdExists(latestGmSltData, laborOperationId)) {
              return of(null);
            }

            // Update with LATEST data
            const updatedGmSltData = this.updateGmSltMenuData(
              latestGmSltData,
              mileageInterval,
              serviceGroup,
              serviceTypeId
            );

            // Return the GM SLT update observable
            return this.gmSltService.updateGmSltRecord(updatedGmSltData);
          }),
          catchError((error) => {
            console.error('Error updating GM SLT after decline:', error);
            return of(null);
          })
        )
        .subscribe();
    }
  }

  removeDeclinedService(menuItem: any, quoteLines: QuoteDetail[] | null, gmSltData: GmSltData) {
    const selectedQuoteLine = (quoteLines ?? []).find((ql) => ql.serviceGroupId === menuItem.displayServiceGroup.id);

    for (let i = 0; i <= this.pendingDeclined.length - 1; i++) {
      if (this.pendingDeclined[i].id === menuItem.displayServiceGroup.id) {
        this.vmService.removePendingDeclinedService(menuItem.displayServiceGroup.id);
        menuItem.displayServiceGroup = menuItem.serviceGroup;
        this.callChangeDetection();
      }
    }

    if (selectedQuoteLine) {
      this.serviceLaneFacade.removeDeclinedQuoteLine(selectedQuoteLine);
      this.serviceLaneFacade.deleteQuoteLineFromServer(selectedQuoteLine);
    }

    // Remove from GM SLT record
    const laborOperationId = menuItem.displayServiceGroup.workOrderLocationOpCode?.toString() ?? '';

    if (laborOperationId && gmSltData) {
      // Fetch latest GM SLT data
      this.gmSltService
        .getGmSltByWorkOrderId(this.extractDestinationId(gmSltData), gmSltData.workOrderId)
        .pipe(
          take(1),
          switchMap((latestGmSltData) => {
            if (!latestGmSltData || !latestGmSltData.menuData) {
              return of(null);
            }

            const updatedGmSltData = this.removeFromGmSltMenuData(latestGmSltData, laborOperationId);

            if (!updatedGmSltData) {
              return of(null);
            }

            return this.gmSltService.updateGmSltRecord(updatedGmSltData);
          }),
          catchError((error) => {
            console.error('Error removing declined service from GM SLT:', error);
            return of(null);
          })
        )
        .subscribe();
    }
  }

  selectService(menuItem: any, serviceGroups: any, serviceGroupRates: any) {
    const dialogRef = this.dialog.open(ServiceSelectionDialogComponent, {
      width: '400px',
      data: { mappedServiceGroups: menuItem.serviceGroup.mappedServiceGroups, serviceGroups, serviceGroupRates },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // A backdrop click or Esc closes the dialog with no result.
      if (result && !result.cancelled) {
        // if (
        //   serviceGroupRates[serviceGroups[result.selectedServiceGroup].serviceGroupRateId] &&
        //   serviceGroupRates[serviceGroups[result.selectedServiceGroup].serviceGroupRateId].rate
        // ) {
        menuItem.displayServiceGroup = serviceGroups[result.selectedServiceGroup];

        //this.changeDetector.detectChanges();
        this.callChangeDetection();
      }
    });
  }

  selectDeclinedReason(menuItem: any, declinedReasons: any[], labourOpData: any, gmSltData: GmSltData) {
    const dialogRef = this.dialog.open(SelectDeclinedReasonDialogComponent, {
      width: '400px',
      data: { declinedReasons: declinedReasons },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // A backdrop click or Esc closes the dialog with no result.
      if (result && !result.cancelled) {
        menuItem.displayServiceGroup.embedded ? null : (menuItem.displayServiceGroup.embedded = {});
        menuItem.displayServiceGroup.embedded.services = [];

        menuItem.displayServiceGroup.services.forEach((service: any) => {
          menuItem.displayServiceGroup.embedded.services.push(labourOpData.services[service]);
        });
        menuItem.displayServiceGroup.embedded.rate =
          labourOpData.serviceGroupRates[menuItem.displayServiceGroup.serviceGroupRateId];
        this.declinedServiceReasons.push({
          serviceGroupId: menuItem.displayServiceGroup.id,
          declinedReason: result.selectedDeclinedReason,
        });

        this.vmService.declinedServicesWithReason.next({
          serviceGroup: menuItem.displayServiceGroup,
          declinedReason: result.selectedDeclinedReason,
        });
        this.declineService(menuItem, gmSltData);
        //this.changeDetector.detectChanges();
        this.callChangeDetection();
      }
    });
  }

  getStyle(serviceGroup: any): object {
    let style = {};

    if (this.savedAccepted.find((e: any) => e.id === serviceGroup.id)) {
      style = { color: '#2E7D32' };
    } else if (this.savedDeclined.find((e: any) => e.id === serviceGroup.id)) {
      style = { color: '#C62828' };
    }

    return style;
  }

  checkServiceGroupStatus(status: string, serviceGroup: any) {
    let statusArray = [];

    switch (status) {
      case '':
        break;
      case 'pending-accepted':
        statusArray = this.pendingAccepted;
        break;
      case 'pending-declined':
        statusArray = this.pendingDeclined;
        break;
      case 'saved-accepted':
        statusArray = this.savedAccepted;
        break;
      case 'saved-declined':
        statusArray = this.savedDeclined;
        break;
    }
    if (statusArray.find((e: any) => e.id === serviceGroup.id)) {
      return true;
    } else {
      return false;
    }
  }

  getTotal(values: any[], rates: any) {
    if (values.length > 0 && rates) {
      return values.reduce(function (prev: any, cur: any) {
        return cur.serviceGroupRateId
          ? prev + (rates[cur.serviceGroupRateId].rate ? rates[cur.serviceGroupRateId].rate : 0)
          : prev;
        //return prev + (rates[cur.serviceGroupRateId].rate ? rates[cur.serviceGroupRateId].rate : 0);
      }, 0);
    } else {
      return 0;
    }
  }

  getPrice(serviceGroup: any, serviceGroupRates: any) {
    if (serviceGroupRates && serviceGroupRates[serviceGroup.serviceGroupRateId] != null) {
      return serviceGroupRates[serviceGroup.serviceGroupRateId].rate;
    } else {
      return 0;
    }
  }

  getRoHistory(roHistory: any, menuItem: any) {
    let foundHistory = false;
    if (menuItem.displayServiceGroup.useFlag === 'M') {
      menuItem.displayServiceGroup.mappedServiceGroups.forEach((sg: string) => {
        if (
          roHistory.findIndex((history: any) => {
            return history.serviceGroupId == sg;
          }) > -1
        ) {
          foundHistory = true;
          return;
        }
      });
    } else {
      if (menuItem.displayServiceGroup.id) {
        roHistory.findIndex((history: any) => {
          return history.serviceGroupId == menuItem.displayServiceGroup.id;
        }) > -1
          ? (foundHistory = true)
          : (foundHistory = false);
      } else {
        foundHistory = true;
      }
    }
    return !foundHistory;
  }

  viewRoHistory(serviceHistory: any[], menuItem: any) {
    let roHistoryWorkOrder: any;
    const serviceGroup = menuItem.displayServiceGroup;
    let roHistoryWorkOrderId: number;
    let mappedServiceGroup: ServiceGroup;
    if (serviceGroup.useFlag == 'M') {
      menuItem.serviceGroup.mappedServiceGroups.forEach((sg: ServiceGroup) => {
        if (serviceHistory.find((s) => s.serviceGroupId == sg.id)) {
          mappedServiceGroup = sg;
          roHistoryWorkOrderId = serviceHistory.find((s) => s.serviceGroupId == sg.id).workOrderId;
          return;
        }
      });
    } else {
      roHistoryWorkOrderId = serviceHistory
        .filter((ele) => ele.serviceGroupId == serviceGroup.id)
        .find((ele) => ele.workOrderId).workOrderId;
    }

    this.fixedOperationsService
      .selectWorkOrder(roHistoryWorkOrderId)
      .pipe(take(1))
      .subscribe((workOrder) => {
        roHistoryWorkOrder = workOrder;
        let dialogRef = this.dialog.open(RoHistoryDialogComponent, {
          width: '400px',
          data: { workOrder: roHistoryWorkOrder, mappedServiceGroup: mappedServiceGroup },
        });

        dialogRef.afterClosed().subscribe((result) => {});
      });
  }

  viewSalesHint(salesHint: string) {
    let dialogRef = this.dialog.open(SalesHintDialogComponent, {
      width: '400px',
      data: { salesHint: salesHint },
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  getDeclinedReason(declinedService: ServiceGroup) {
    const declinedReason = this.declinedServiceReasons.find((reason: any) => {
      return reason.serviceGroupId === declinedService.id;
    });
    if (declinedReason) {
      return ` (${declinedReason.declinedReason.reason})`;
    }
  }

  save(
    pendingAccepted: any,
    pendingDeclined: any,
    labourOpData: any,
    vehicle: Vehicle,
    transaction: any,
    gmSltData: GmSltData,
    oem: string
  ) {
    let toggledAnimation = false;
    this.saving = true;
    const total = pendingAccepted.length + pendingDeclined.length;
    if (this.routeAppointmentId) {
      combineLatest([this.vmService.selectPendingAcceptedServices(), this.vmService.selectPendingDeclinedServices()])
        .pipe(
          map(([acceptedPending, declinedPending]: any) => {
            return acceptedPending.concat(declinedPending);
          })
        )

        .subscribe((pending: any) => {
          this.requestingPending = `${pending.length} requests left!`;
          // this.saveProgress =
          //   total - pending.length == 0
          //     ? 0
          //     : (total - pending.length) / total == 0 ? 100 : pending.length / total * 100;
          this.saveProgress = (pending.length / total) * 100;
          //this.changeDetector.detectChanges();
          this.callChangeDetection();
          if (pending.length === 0 && !toggledAnimation) {
            this.saving = false;
            this.toggleAnimation();
            toggledAnimation = true;
            //this.changeDetector.detectChanges();
            this.callChangeDetection();
            this.vmService.scheduleSavedNotification.next(true);
          }
          // setTimeout(() => {
          //   this.saving = false;
          //   if (pending.length === 0 && !toggledAnimation) {
          //     this.toggleAnimation();
          //     toggledAnimation = true;
          //     this.changeDetector.detectChanges();
          //   }
          // }, 400);
        });

      pendingAccepted.forEach((serviceGroup: any) => {
        let appointmentCase = new AppointmentCase();
        appointmentCase.appointmentId = this.routeAppointmentId;
        appointmentCase.requestDescription = serviceGroup.name + ' (';
        serviceGroup.services.forEach((service: any) => {
          appointmentCase.requestDescription += ' ' + labourOpData.services[service].name;
        });
        appointmentCase.requestDescription += ')';
        appointmentCase.addedFromServiceSchedule = true;
        appointmentCase.caseFromQuote = false;
        appointmentCase.estimatedTime = serviceGroup.estimatedTime;
        appointmentCase.opCode = serviceGroup.workOrderLocationOpCode;
        appointmentCase.recommendedHours = serviceGroup.serviceGroupRateId
          ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].recommendedHours != null
            ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].recommendedHours
            : serviceGroup.recommendedHours
          : serviceGroup.recommendedHours;

        appointmentCase.serviceGroupId = serviceGroup.id;
        appointmentCase.technicianGroupId = serviceGroup.technicianGroupId;
        appointmentCase.technicianGroupName = serviceGroup.technicianGroupName;
        appointmentCase.technicianId = serviceGroup.technicianId;
        appointmentCase.warrantySubmissionTypeId =
          serviceGroup.warrantySubmissionTypeId != null && serviceGroup.warrantySubmissionTypeId.trim() != ''
            ? serviceGroup.warrantySubmissionTypeId
            : 'CP';
        appointmentCase.partsKitMaintenanceId =
          serviceGroup.maintenancePartKits?.length > 0 ? serviceGroup.maintenancePartKits[0] : null;

        // appointmentCase.startTime = new Date();
        // appointmentCase.startTime.setDate(16);
        // appointmentCase.startTime.setHours(8, 30);

        this.fixedOperationsService.createAppointmentCase(appointmentCase);
      });

      pendingDeclined.forEach((declined: ServiceGroup) => {
        const declinedService: ServiceAppointmentDeclinedMpviCode = new ServiceAppointmentDeclinedMpviCode();
        declinedService.appointmentId = this.routeAppointmentId;
        declinedService.opCode = declined.workOrderLocationOpCode ? declined.workOrderLocationOpCode : declined.id;

        const declinedReason = this.declinedServiceReasons.find((reason: any) => {
          return reason.serviceGroupId === declined.id;
        });
        if (declinedReason) {
          declinedService.declinedReason = declinedReason.declinedReason.id;
        }
        this.fixedOperationsService.createAppointmentDeclinedService(declinedService, declined.rooftopId);
      });
    }

    if (this.routeWorkOrderId) {
      combineLatest([this.vmService.selectPendingAcceptedServices(), this.vmService.selectPendingDeclinedServices()])
        .pipe(
          map(([acceptedPending, declinedPending]: any) => {
            return acceptedPending.concat(declinedPending);
          })
        )

        .subscribe((pending: any) => {
          this.requestingPending = `${pending.length} requests left!`;
          // this.saveProgress =
          //   total - pending.length == 0
          //     ? 0
          //     : (total - pending.length) / total == 0 ? 100 : pending.length / total * 100;
          // eslint-disable-next-line
          this.saveProgress = 100 - (pending.length / total) * 100;
          this.changeDetector.detectChanges();

          if (pending.length === 0 && !toggledAnimation) {
            this.saving = false;
            this.toggleAnimation();
            toggledAnimation = true;
            // this.changeDetector.detectChanges();
            this.callChangeDetection();
          }
          // setTimeout(() => {
          //   this.saving = false;
          //   if (pending.length === 0 && !toggledAnimation) {
          //     this.toggleAnimation();
          //     toggledAnimation = true;
          //     this.changeDetector.detectChanges();
          //   }
          // }, 400);
        });

      pendingAccepted.forEach(async (serviceGroup: any) => {
        let workOrderCase = new WorkOrderCase();
        workOrderCase.workOrderId = this.routeWorkOrderId;

        workOrderCase.addedFromServiceSchedule = true;
        workOrderCase.allowMultipleTechFlag = '0';
        workOrderCase.approvedFlag = '1';
        workOrderCase.caseStatusId = 'N';
        workOrderCase.chargeoutRateBasisId = 1;
        //workOrderCase.employeeId =
        workOrderCase.estimatedTime = serviceGroup.estimatedTime;

        workOrderCase.internalTransTypeId = null;
        workOrderCase.labourOp = serviceGroup.workOrderLocationOpCode;
        workOrderCase.labourOpFlag = '1';
        if (transaction.requestSymptomsPrintedFlag == '1') {
          workOrderCase.note = 'Added Operation: ';
        } else {
          workOrderCase.note = '';
        }
        workOrderCase.note += serviceGroup.name + ' (';
        serviceGroup.services.forEach((service: any) => {
          workOrderCase.note += ' ' + labourOpData.services[service].name;
        });
        workOrderCase.note += ')';
        //workOrderCase.partKitsMaintenanceId =
        workOrderCase.recommendedHours = serviceGroup.serviceGroupRateId
          ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].recommendedHours != null
            ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].recommendedHours
            : serviceGroup.recommendedHours
          : serviceGroup.recommendedHours;

        workOrderCase.serviceGroupId = serviceGroup.id;
        // eslint-disable-next-line max-len
        workOrderCase.shopSupplyFlag = '1'; //Get asscu_shop_flag if 0 then 0. If 1 then we check sgred_shop_flag if null/''/'1' we use '1' ELSE '0'
        workOrderCase.technicianGroupId = serviceGroup.technicianGroupId;
        workOrderCase.technicianGroupName = serviceGroup.technicianGroupName;
        workOrderCase.technicianId = serviceGroup.technicianId;
        workOrderCase.technicianRateBasisId = 1;

        //workOrderCase.warrantyClaimTypeId = '1';
        //wstct_wcltp_id (from warranty_submission_type_to_claim_type_mapping - passing in warstId and oem)

        workOrderCase.warrantySubmissionTypeId =
          serviceGroup.warrantySubmissionTypeId != null && serviceGroup.warrantySubmissionTypeId.trim() != ''
            ? serviceGroup.warrantySubmissionTypeId
            : 'CP';

        if (workOrderCase.warrantySubmissionTypeId == 'WC') {
          workOrderCase.warrantyClaimTypeId = await this.getWarrantyClaimType('WC', 'GM');
        } else {
          workOrderCase.warrantyClaimTypeId = null;
        }
        this.fixedOperationsService.createWorkOrderCase(workOrderCase, vehicle);
      });

      pendingDeclined.forEach((declined: ServiceGroup) => {
        //let declinedService: ServiceAppointmentDeclinedMpviCode = new ServiceAppointmentDeclinedMpviCode();
        const declinedService: WorkorderDeclinedMpviCode = new WorkorderDeclinedMpviCode();
        declinedService.workOrderId = this.routeWorkOrderId;
        declinedService.opCode = declined.workOrderLocationOpCode ? declined.workOrderLocationOpCode : declined.id;
        declinedService.classificationId = 1;
        declinedService.printFlag = true;
        const declinedReason = this.declinedServiceReasons.find((reason: any) => {
          return reason.serviceGroupId === declined.id;
        });
        if (declinedReason) {
          declinedService.declinedReasonId = declinedReason.declinedReason.id;
        }

        this.fixedOperationsService.createWorkOrderDeclinedService(declinedService, declined.rooftopId);
      });
    }

    if (
      oem === 'GM' &&
      (pendingAccepted.length > 0 || pendingDeclined.length > 0) &&
      gmSltData &&
      (this.routeWorkOrderId || this.workOrderId)
    ) {
      this.vmService.servicesDue$.pipe(take(1)).subscribe((servicesDue: any) => {
        if (servicesDue.length > 0) {
          // Fetch latest GM SLT data first to avoid overwriting
          this.gmSltService
            .getGmSltByWorkOrderId(this.extractDestinationId(gmSltData), gmSltData.workOrderId)
            .pipe(
              take(1),
              switchMap((latestGmSltData) => {
                const newMenuData: MenuData[] = [];

                if (pendingAccepted.length > 0) {
                  pendingAccepted.forEach((acceptedService: any) => {
                    const serviceDue = servicesDue.find(
                      (serviceDue: any) => serviceDue.serviceGroupId === acceptedService.id
                    );
                    if (!serviceDue) {
                      return;
                    }
                    newMenuData.push({
                      menuType: serviceDue.serviceTypeId === 1 || serviceDue.serviceTypeId === 2 ? 'F' : 'D',
                      mileageMarker: serviceDue.mileage.toString(),
                      laborOperationId: acceptedService.workOrderLocationOpCode,
                    });
                  });
                }

                if (pendingDeclined.length > 0) {
                  pendingDeclined.forEach((declinedService: any) => {
                    const serviceDue = servicesDue.find(
                      (serviceDue: any) => serviceDue.serviceGroupId === declinedService.id
                    );
                    if (!serviceDue) {
                      return;
                    }
                    newMenuData.push({
                      menuType: serviceDue.serviceTypeId === 1 || serviceDue.serviceTypeId === 2 ? 'F' : 'D',
                      mileageMarker: serviceDue.mileage.toString(),
                      laborOperationId: declinedService.workOrderLocationOpCode,
                    });
                  });
                }

                // Merge with existing menuData
                const existingMenuData = latestGmSltData.menuData || [];
                const mergedMenuData = existingMenuData
                  .concat(newMenuData)
                  .filter(
                    (item, index, self) => index === self.findIndex((t) => t.laborOperationId === item.laborOperationId)
                  );

                const updatedGmSltData = {
                  ...latestGmSltData,
                  menuData: mergedMenuData,
                };

                return this.gmSltService.updateGmSltRecord(updatedGmSltData);
              })
            )
            .subscribe();
        }
      });
    }
  }

  async getWarrantyClaimType(submissionType: string, oem: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.apiService
        .get(`v/1/fixed-operations/warranty-submission-type-to-claim-type-mappings`, {
          params: { warrantySubmissionTypeId: submissionType, manufacturerId: oem },
        })
        .subscribe(
          (value) => {
            if (value.length > 0) {
              resolve(value[0].warrantyClaimTypeId);
            } else {
              reject();
            }
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  print(): void {
    window.print();
  }

  toggleCollapse(group: any[], groupUiObject: any) {
    groupUiObject.collapsed = !groupUiObject.collapsed;
    group.forEach((group) => (group.collapsed = groupUiObject.collapsed));
  }

  disableRemoveServiceButton(disable: boolean) {
    this.disableRemoveServiceButton$.next(disable);
  }

  disableAddServiceButton(disable: boolean) {
    this.disableAddServiceButton$.next(disable);
  }

  private checkOrCreateWorkOrder(
    transaction: any,
    employee: Employee,
    systemControls: any,
    combinedTax: any
  ): Observable<number> {
    const appointmentId = transaction.id;

    return this.apiService
      .get<any[]>('v/1/fixed-operations/work-orders', {
        params: { appointmentId: appointmentId.toString() },
      })
      .pipe(
        switchMap((workOrders) => {
          if (workOrders && workOrders.length > 0) {
            return of(workOrders[0].id);
          } else {
            return this.createReservedWorkOrder(this.vehicle, employee, systemControls, combinedTax);
          }
        })
      );
  }

  private createReservedWorkOrder(
    vehicle: Vehicle,
    employee: Employee,
    systemControls: any,
    combinedTax: any
  ): Observable<number> {
    const workOrder: WorkOrder = {
      ...new WorkOrder(),
      appointmentId: this.transaction.id,
      appointmentBookingEmployeeId: this.transaction.employeeId,
      appointmentDateTime: this.transaction.dateTime,
      autoAssign: '1',
      createdByVipApp: true,
      followUp: '1',
      hazardChargeFlag: false,
      includePricingInfoInCustomerMessage: false,
      inspectionComplete: false,
      phoneReady: '0',
      printInternalFlag: '1',
      ready: '0',
      updatedFlag: '1',
      walkaroundComplete: false,
      warrantyAccepted: '0',
      warrantyAuditDone: '0',
      warrantyReportFlag: '1',
      fordCudbSent: false,
      gmDdsSent: false,
      ivhUsed: false,
      toyotaCvshSent: false,
      body: vehicle.body,
      chassis: '',
      deliveryDate: vehicle.deliveryDate,
      engine: vehicle.engine,
      last6Vin: vehicle.vin.slice(-6),
      leaseOperatorId: vehicle.leaseVehicleOperatorId,
      licenseNumber: this.transaction.licenseNumber,
      printVehicleWarrantyId: vehicle.extendedWarrantyId,
      reference: vehicle.stockNumber,
      serialNumber: vehicle.vin,
      series: vehicle.model,
      unitOfMeasurementId: vehicle.unitOfMeasureId,
      vehicleCategory: vehicle.categoryDescription,
      vehicleId: vehicle.id,
      vehicleMake: vehicle.make,
      vehicleStatusId: vehicle.statusCode,
      vehicleTypeId: vehicle.embedded?.['type']?.classId,
      year: vehicle.year && vehicle.year.toString() !== '1900' ? vehicle.year.toString() : null,
      employeeId: this.transaction.serviceAdvisorId,
      rooftopId: employee.rooftopId,
      customerId: this.transaction.customerId,
      contactPhoneNumber: this.transaction.phoneNumber,
      currencyId: systemControls['CL_CURRENCY']?.value,
      electronicWorkOrderFlag: systemControls['CL_ELECTRONIC_WORK_ORDER_FLAG']?.value || '0',
      shopSuppliesPercent: systemControls['CL_SHOP_SUPPLIES_PERCENT']?.value,
      gstWhenCreated: combinedTax?.gstTax ? combinedTax.gstTax.gstPercent : null,
      pstWhenCreated: combinedTax?.pstTax ? combinedTax.pstTax.pstPercent : null,
      dateIn: null as Date | null,
      labourRateHour: null,
      statusId: 'R', // Reserved
      typeId: '1',
    };

    return this.apiService
      .post<WorkOrder>('v/1/fixed-operations/work-orders', workOrder)
      .pipe(map((createdWorkOrder) => createdWorkOrder.id));
  }

  private extractDestinationId(gmSltData: GmSltData): string | null {
    if (!gmSltData?.PK || !gmSltData.PK.includes('#')) {
      console.warn('Invalid GM SLT PK format:', gmSltData?.PK);
      return null;
    }
    return gmSltData.PK.split('#')[1];
  }

  private getLaborOperationId(serviceGroup: any): string | null {
    const laborOperationId = serviceGroup.workOrderLocationOpCode?.toString() ?? '';

    if (!laborOperationId) {
      console.log('No laborOperationId found, skipping GM SLT update');
      return null;
    }

    return laborOperationId;
  }

  private laborOperationIdExists(gmSltData: GmSltData, laborOperationId: string): boolean {
    return gmSltData.menuData?.some((item) => item.laborOperationId === laborOperationId) ?? false;
  }

  private removeFromGmSltMenuData(gmSltData: GmSltData, laborOperationId: string): GmSltData | null {
    if (!gmSltData?.menuData) {
      return null;
    }

    const updatedMenuData = gmSltData.menuData.filter((item) => item.laborOperationId !== laborOperationId);

    // Only update if something was actually removed
    if (updatedMenuData.length === gmSltData.menuData.length) {
      console.log('laborOperationId not found in GM SLT data, nothing to remove');
      return null;
    }

    return {
      ...gmSltData,
      menuData: updatedMenuData,
    };
  }
}
